/**
 * 认证服务
 * 处理所有认证相关的API调用
 *
 * @module services/authService
 */

import httpClient from "@shared/http/createHttpClient";
import type { User } from "@shared/stores";
import { createModuleLogger } from "@shared/utils/Logger";
const logger = createModuleLogger("authService");

type RequestError = Error & { status?: number; errorCode?: string };

function createRequestError(
  status: number | undefined,
  message: string,
  errorCode?: string,
): RequestError {
  const error = new Error(message) as RequestError;
  error.name = status === 401 ? "AuthError" : "HttpError";
  error.status = status;
  if (errorCode) error.errorCode = errorCode;
  return error;
}

async function parseResponseError(error: any): Promise<RequestError> {
  const response = error.response;
  const status = response?.status;

  let message = `HTTP ${status}: ${response?.statusText || "未知错误"}`;
  let errorCode: string | undefined;

  try {
    const errorData = response?.data;
    // 兼容多种后端响应格式
    const detail = errorData?.detail || {};
    errorCode =
      errorData?.error_code ||
      (typeof detail === "object" ? detail.error_code : undefined);
    message =
      errorData?.error_message ||
      errorData?.message ||
      (typeof detail === "object" ? detail.message : detail) ||
      message;
  } catch {
    // 保留默认错误信息
  }

  // 兼容旧后端/未重启后端
  if (!errorCode) {
    if (status === 403) {
      if (message.includes("首次登录") || message.includes("密码已被重置")) {
        errorCode = "PASSWORD_CHANGE_REQUIRED";
      } else if (message.includes("密码已过期")) {
        errorCode = "PASSWORD_EXPIRED";
      } else if (message.includes("未完成密码初始化")) {
        errorCode = "PASSWORD_NOT_INITIALIZED";
      } else if (message.includes("pgyAdmin")) {
        // 平台托管账号：改密必须回到控制面
        errorCode = "MANAGED_BY_CONTROL_PLANE";
      } else if (
        message.includes("账户不存在") ||
        message.includes("账号不存在")
      ) {
        errorCode = "USER_NOT_PROVISIONED";
      }
    } else if (status === 423) {
      errorCode = "ACCOUNT_LOCKED";
    } else if (status === 429) {
      errorCode = "RATE_LIMITED";
    }
  }

  return createRequestError(status, message, errorCode);
}

// ==================== 类型定义 ====================

/**
 * 工作台登录凭据（唯一权威形态）
 *
 * - `username`：前端统一 `trim().toLowerCase()` 归一后再提交
 * - `tenant_code`：单位编码，只 `trim()`，保留大小写做精确匹配；
 *   空字符串视为未提供，序列化为 `null`
 * - 临床账号必须填单位编码；平台/单位管理员可留空
 */
export interface WorkbenchCredentials {
  username: string;
  password: string;
  tenant_code?: string | null;
}

/**
 * 发送验证码请求（用户名 + 密码 + 可选单位编码）
 */
export interface SendCodeRequest extends WorkbenchCredentials {}

/**
 * 发送验证码响应
 */
export interface SendCodeResponse {
  success: boolean;
  message: string;
  debug_code?: string; // 开发环境返回的测试验证码
  wait_seconds?: number; // 后端限流提示的剩余等待秒数
}

/**
 * 登录请求（用户名 + 密码 + 验证码）
 */
export interface LoginRequest extends WorkbenchCredentials {
  verification_code: string;
  totp_code?: string;
}

/**
 * 首次改密请求。
 *
 * 注意：请求体没有 `old_password`，`password` 即「当前密码」；
 * 后端 `extra="forbid"`，多传字段会 422。
 */
export interface FirstPasswordChangeRequest {
  username: string;
  password: string;
  tenant_code?: string | null;
  verification_code: string;
  new_password: string;
}

/**
 * 归一化用户名：去空白 + 转小写（与后端归一规则保持一致）
 */
export function normalizeUsername(username: string): string {
  return String(username ?? "")
    .trim()
    .toLowerCase();
}

/**
 * 归一化单位编码：只去空白、保留大小写；空字符串视为未提供（null）
 */
export function normalizeTenantCode(tenantCode?: string | null): string | null {
  const trimmed = String(tenantCode ?? "").trim();
  return trimmed ? trimmed : null;
}

/**
 * 组装工作台凭据请求体：统一归一化，保证不出现 `phone_number`
 */
function toCredentialBody(credentials: WorkbenchCredentials): Required<
  Pick<WorkbenchCredentials, "username" | "password">
> & {
  tenant_code: string | null;
} {
  return {
    username: normalizeUsername(credentials.username),
    password: credentials.password ?? "",
    tenant_code: normalizeTenantCode(credentials.tenant_code),
  };
}

/**
 * HIS 免密登录票据兑换响应
 */
export interface HisSsoExchangeResponse {
  success: boolean;
  message: string;
  access_token?: string;
  token_type?: string;
  user?: User;
  redirect?: string;
}

/**
 * 登录响应
 */
export interface LoginResponse {
  success: boolean;
  message: string;
  access_token: string;
  token_type?: string;
  user: User;
  is_new_user: boolean;
}

/**
 * 更新资料请求
 */
export interface UpdateProfileRequest {
  nickname?: string;
  email?: string;
  avatar_url?: string;
}

/**
 * 更新资料响应
 */
export interface UpdateProfileResponse {
  success: boolean;
  message: string;
  user?: User;
  data?: {
    user: User;
  };
}

/**
 * 获取用户信息响应
 */
export interface UserInfoResponse {
  success: boolean;
  message?: string;
  user?: User;
  data?: {
    user: User;
  };
}

/**
 * 健康检查响应
 */
export interface HealthCheckResponse {
  status: string;
  timestamp?: string;
  version?: string;
  [key: string]: unknown;
}

// ==================== AuthService类 ====================

/**
 * 认证服务类
 * 负责所有用户认证相关的API调用
 */
class AuthService {
  /**
   * API基础URL（相对路径，createHttpClient 已处理 /api/v2 前缀）
   */
  private readonly baseUrl = "/simple/auth";

  /**
   * 获取完整的API URL
   * @param endpoint - API端点
   * @returns 完整URL
   */
  private getUrl(endpoint: string): string {
    // 确保endpoint以/开头
    const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    return `${this.baseUrl}${path}`;
  }

  /**
   * 发送验证码（用户名 + 密码 + 可选单位编码）
   * @param credentials - 工作台登录凭据
   */
  async sendVerificationCode(
    credentials: WorkbenchCredentials,
  ): Promise<SendCodeResponse> {
    const body: SendCodeRequest = toCredentialBody(credentials);

    try {
      return await httpClient.post<SendCodeResponse>(
        this.getUrl("/send-code"),
        body,
        // 预认证端点：401 = 凭据错误而非会话过期，不进入恢复链，
        // 由调用方展示文案（否则登录页错密码会走 refresh→重放→登出跳转链）
        { silent: true, _skipAuthRecovery: true },
      );
    } catch (error) {
      throw await parseResponseError(error);
    }
  }

  /**
   * 验证码登录（用户名 + 密码 + 验证码 + 可选单位编码）
   * @param credentials - 工作台登录凭据（含验证码）
   * @returns 登录结果
   */
  async loginWithCode(credentials: LoginRequest): Promise<LoginResponse> {
    const body: LoginRequest = {
      ...toCredentialBody(credentials),
      verification_code: credentials.verification_code,
    };
    if (credentials.totp_code) body.totp_code = credentials.totp_code;

    let data: LoginResponse;
    try {
      data = await httpClient.post<LoginResponse>(this.getUrl("/login"), body, {
        silent: true,
        // 同上：验证码/凭据错误的 401 交由调用方翻译
        _skipAuthRecovery: true,
      });
    } catch (error) {
      throw await parseResponseError(error);
    }

    logger.debug("✅ 登录成功:", {
      success: data.success,
      userId: data.user?.id,
      isNewUser: data.is_new_user,
    });

    return data;
  }

  /**
   * HIS 免密登录：拿一次性 ticket 兑换蒲公英会话。
   * 调用成功后会在响应头里写入 refresh_token Cookie，前端只需消费 access_token / user。
   */
  async exchangeHisSsoTicket(ticket: string): Promise<HisSsoExchangeResponse> {
    return await httpClient.post<HisSsoExchangeResponse>(
      this.getUrl("/his-sso/exchange"),
      { ticket },
      // 预认证端点：票据失效的 401 不应触发 refresh/登出跳转
      { _skipAuthRecovery: true },
    );
  }

  /**
   * 首次登录/后台重置后，通过「当前密码 + 短信验证码」设置新密码。
   * 请求体固定 5 字段，不含 `old_password`（后端 extra="forbid"）。
   */
  async firstPasswordChange(payload: FirstPasswordChangeRequest): Promise<{
    success: boolean;
    message: string;
  }> {
    const body = {
      ...toCredentialBody(payload),
      verification_code: payload.verification_code,
      new_password: payload.new_password,
    };
    try {
      return await httpClient.post(
        this.getUrl("/first-password-change"),
        body,
        // 预认证端点：当前密码/验证码错误为 401，交由调用方展示
        { silent: true, _skipAuthRecovery: true },
      );
    } catch (error) {
      throw await parseResponseError(error);
    }
  }

  /**
   * 获取当前用户信息
   * @param token - 访问令牌
   * @returns 用户信息
   */
  async fetchUserInfo(token: string): Promise<UserInfoResponse> {
    const data = await httpClient.get<UserInfoResponse>(this.getUrl("/me"), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    logger.debug("✅ 获取用户信息成功:", {
      success: data.success,
      userId: data.user?.id || data.data?.user?.id,
    });

    return data;
  }

  /**
   * 更新用户资料
   * @param token - 访问令牌
   * @param profileData - 要更新的资料
   * @returns 更新结果
   */
  async updateProfile(
    token: string,
    profileData: UpdateProfileRequest,
  ): Promise<UpdateProfileResponse> {
    const data = await httpClient.put<UpdateProfileResponse>(
      this.getUrl("/me"),
      profileData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    logger.debug("✅ 更新用户资料成功:", {
      success: data.success,
      updates: Object.keys(profileData),
    });

    return data;
  }

  /**
   * 登出
   */
  async logout(): Promise<void> {
    try {
      await httpClient.post(this.getUrl("/logout"), undefined, {
        _skipGlobalError: true, // 登出失败不弹窗
      });
    } catch {
      // 登出失败静默处理
      console.warn("⚠️ 登出接口调用失败");
    }
  }

  /**
   * 检查系统健康状态
   * @returns 健康状态
   */
  async checkHealth(): Promise<HealthCheckResponse> {
    return await httpClient.get<HealthCheckResponse>(this.getUrl("/health"));
  }

  /**
   * 刷新访问令牌
   * @returns 新的访问令牌
   */
  async refreshToken(): Promise<{
    success: boolean;
    access_token?: string;
    token_type?: string;
    message?: string;
  }> {
    // _skipAuthRecovery：refresh 自身 401 绝不能再进入恢复链——恢复链会 await
    // refreshSession() 的在途 promise，而该 promise 正等待本次 refresh 落定，
    // 形成循环等待死锁（登录页错密码曾因此永久卡在「发送验证码中...」）。
    return await httpClient.post(this.getUrl("/refresh"), undefined, {
      _skipAuthRecovery: true,
    });
  }
}

// ==================== 导出 ====================

/**
 * 认证服务单例
 */
export const authService = new AuthService();

/**
 * 默认导出
 */
export default authService;
